import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  fetchUserById,
  deleteUser,
  setUserSuspended,
  adminUpdateUser,
  updateUserFeatureFlags,
} from "../../store/slices/usersSlice";

export const useUserProfile = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { showToast } = useToast();
  const { userId } = useParams<{ userId: string }>();

  const {
    currentUser: user,
    isLoading: loading,
    error,
  } = useAppSelector((state: any) => state.users);
  const { user: currentUser } = useAppSelector((state: any) => state.auth);
  // The admin panel itself is gated behind admin login (AuthContext), so any
  // authenticated session here is an admin. We still keep the Redux check as
  // a belt-and-braces fallback.
  const { user: authContextUser, isAuthenticated } = useAuth();

  // Local state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if current user is admin
  const isAdmin =
    isAuthenticated ||
    authContextUser?.role === "admin" ||
    currentUser?.role === "admin" ||
    currentUser?.isAdmin;

  // Load user data
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserById(userId));
    }
  }, [dispatch, userId]);

  const handleRefresh = () => {
    if (userId) {
      dispatch(fetchUserById(userId));
    }
  };

  // Handlers
  const handleEditUser = () => {
    setShowEditModal(true);
  };

  const handleDeleteUser = () => {
    setShowDeleteModal(true);
  };

  const handleSuspendUser = () => {
    setShowSuspendModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!user || !isAdmin) return;

    setIsLoading(true);
    try {
      // .unwrap() rethrows the rejected thunk so the catch below actually
      // runs on failure (a bare dispatch swallows the error).
      await dispatch(deleteUser(user._id)).unwrap();
      showToast("User deleted successfully", "success");
      navigate("/users");
    } catch (error: any) {
      showToast(
        `Failed to delete user: ${error?.message || error}`,
        "error"
      );
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleConfirmSuspend = async () => {
    if (!user || !isAdmin) return;

    const next = !user.isSuspended;
    setIsLoading(true);
    try {
      await dispatch(
        setUserSuspended({ id: user._id, suspended: next })
      ).unwrap();
      showToast(
        `User ${next ? "suspended" : "reactivated"} successfully`,
        "success"
      );
    } catch (error: any) {
      showToast(
        `Failed to ${next ? "suspend" : "reactivate"} user: ${
          error?.message || error
        }`,
        "error"
      );
    } finally {
      setIsLoading(false);
      setShowSuspendModal(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleSaveEdit = async (formData: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    username?: string;
    bio?: string;
    gender?: string;
  }) => {
    if (!user || !isAdmin) return;
    setIsLoading(true);
    try {
      // email/status aren't admin-editable here; only send recognised fields.
      await dispatch(
        adminUpdateUser({
          id: user._id,
          data: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: formData.phoneNumber,
            username: formData.username,
            bio: formData.bio,
            gender: formData.gender,
          },
        })
      ).unwrap();
      showToast("User updated successfully", "success");
      setShowEditModal(false);
    } catch (error: any) {
      showToast(
        `Failed to update user: ${error?.message || error}`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleCloseSuspendModal = () => {
    setShowSuspendModal(false);
  };

  const [marketingTogglePending, setMarketingTogglePending] = useState(false);
  const handleToggleMarketing = async (next: boolean) => {
    if (!user || !isAdmin) return;
    setMarketingTogglePending(true);
    try {
      await dispatch(
        updateUserFeatureFlags({
          id: user._id,
          featureFlags: { marketing: next },
        })
      ).unwrap();
      // Re-fetch so the header reflects the new flag without a manual page
      // reload. The feature-flags endpoint returns a trimmed user object
      // (id/email/flags only), so relying on its payload alone left other
      // currentUser fields stale and the toggle visually out of sync until
      // refresh.
      await dispatch(fetchUserById(user._id));
      showToast(
        `Marketing access ${next ? "enabled" : "disabled"}`,
        "success"
      );
    } catch (error: any) {
      showToast(
        `Failed to update marketing access: ${error?.message || error}`,
        "error"
      );
    } finally {
      setMarketingTogglePending(false);
    }
  };

  return {
    // Data
    user,
    trackingUser: location?.state?.trackingUser,
    loading,
    error,
    isAdmin,

    // Modal states
    showEditModal,
    showDeleteModal,
    showSuspendModal,
    isLoading,

    // Handlers
    handleEditUser,
    handleDeleteUser,
    handleSuspendUser,
    handleRefresh,
    handleConfirmDelete,
    handleConfirmSuspend,
    handleCloseEditModal,
    handleSaveEdit,
    handleCloseDeleteModal,
    handleCloseSuspendModal,

    // Feature flags
    marketingTogglePending,
    handleToggleMarketing,
  };
};
